import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import SideNavigation from '@/components/layout/SideNavigation';
import { usePessoaConfig } from '@/hooks/usePessoaConfig';
import { useAppHeight } from '@/hooks/useAppHeight';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  resolvePresetUrl,
  resolvePresetTheme,
  themeToFgColor,
  themeToTextShadow,
  themeToLogoShadow,
  themeToBackdropFilter,
  themeToWidgetBackdropFilter,
  themeToWidgetTextShadow,
} from '@/lib/bgPresets';

interface AppShellProps {
  currentView: string;
  currentSubview?: string | null;
  onNavigate: (...args: any[]) => void;
  onOpenDrawer?: (type: string, id: string) => void;
  children: React.ReactNode;
}

export default function AppShell({ currentView, currentSubview, onNavigate, onOpenDrawer, children }: AppShellProps) {
  // FIX iOS: Altura real do viewport (resolve 100vh bug no iOS Safari)
  const appHeight = useAppHeight();

  // Config do perfil do usuário (valor default do menu pinado e background)
  const { menuPinado: menuPinadoPerfil, homeBg } = usePessoaConfig();
  const { pessoa, loading: authLoading } = useAuth();

  // Wallpaper só pode pintar a imagem real quando o profile está carregado.
  // Durante auth boot (F5 inclusive), homeBg/pessoa estão em valores default
  // ainda não persistidos — pintar AUTO agora produziria uma imagem que muda
  // assim que pessoa.id chega. Hold em cor neutra evita o flash. Padrão de
  // boot atômico (Linear, Stripe, Vercel): nada de tinta intermediária.
  const wallpaperReady = !authLoading && !!pessoa?.id;
  const LOADING_BG = '#0f172a';

  // Resolver signed URL para uploads customizados
  const [resolvedImageUrl, setResolvedImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (homeBg?.tipo !== 'imagem') {
      setResolvedImageUrl(null);
      return;
    }
    const valor = homeBg.valor;
    // Sem valor = sem imagem (upload pendente)
    if (!valor) {
      setResolvedImageUrl(null);
      return;
    }
    // Se já é uma URL completa (signed URL do preview), usar direto
    if (valor.startsWith('http') || valor.startsWith('blob:')) {
      setResolvedImageUrl(valor);
      return;
    }
    // Storage path → resolver signed URL
    let cancelled = false;
    supabase.storage
      .from('documento')
      .createSignedUrl(valor, 86400) // 24h
      .then(({ data, error }) => {
        if (!cancelled && !error && data?.signedUrl) {
          setResolvedImageUrl(data.signedUrl);
        }
      });
    return () => { cancelled = true; };
  }, [homeBg?.tipo, homeBg?.valor]);

  // Calcular estilos de background para a Home (base + filtro + overlay)
  const homeBackgroundStyle = useMemo(() => {
    if (!wallpaperReady) {
      return {
        base: { backgroundColor: LOADING_BG, backgroundImage: 'none' },
        filter: 'none',
        overlay: null,
      };
    }
    if (!homeBg) {
      return {
        base: {
          backgroundImage: `url(${resolvePresetUrl('default', pessoa?.id)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        },
        filter: 'none',
        overlay: null,
      };
    }

    let base;
    switch (homeBg.tipo) {
      case 'cor':
        base = {
          backgroundColor: homeBg.valor,
          backgroundImage: 'none',
        };
        break;
      case 'imagem':
        base = {
          backgroundImage: resolvedImageUrl ? `url(${resolvedImageUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        };
        break;
      case 'preset':
      default:
        base = {
          backgroundImage: `url(${resolvePresetUrl(homeBg.valor, pessoa?.id)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        };
        break;
    }

    // Filtro CSS (só para imagens)
    const isImg = homeBg.tipo === 'imagem' || homeBg.tipo === 'preset';
    let filter = 'none';
    if (isImg && homeBg.filtro) {
      if (homeBg.filtro === 'grayscale') filter = 'grayscale(100%)';
      else if (homeBg.filtro === 'sepia') filter = 'sepia(100%)';
    }

    // Overlay de cor (só para imagens com opacidade > 0)
    let overlay = null;
    if (isImg && homeBg.overlay_opacidade && homeBg.overlay_opacidade > 0) {
      overlay = {
        color: homeBg.overlay_cor || '#000000',
        opacity: homeBg.overlay_opacidade,
      };
    }

    return { base, filter, overlay };
  }, [wallpaperReady, homeBg, resolvedImageUrl, pessoa?.id]);

  // P3-007: pin state persists across F5 via localStorage.
  // Order of precedence: localStorage > profile config > true (default).
  // Profile config still initializes the value on first load (when
  // localStorage is empty) so a new user inherits their saved preference.
  const [isPinned, setIsPinned] = useState<boolean>(() => {
    if (typeof window === 'undefined') return menuPinadoPerfil ?? true;
    const stored = window.localStorage.getItem('joshua_sidebar_pinned');
    if (stored !== null) return stored === 'true';
    return menuPinadoPerfil ?? true;
  });
  const initializedRef = useRef(false);

  // Sync from profile only when localStorage hasn't claimed precedence.
  useEffect(() => {
    if (initializedRef.current || menuPinadoPerfil === undefined) return;
    const stored = window.localStorage.getItem('joshua_sidebar_pinned');
    if (stored === null) {
      setIsPinned(menuPinadoPerfil);
    }
    initializedRef.current = true;
  }, [menuPinadoPerfil]);

  // Persist any session-level toggle to localStorage.
  useEffect(() => {
    window.localStorage.setItem('joshua_sidebar_pinned', String(isPinned));
  }, [isPinned]);

  // Estado de hover
  const [isHovered, setIsHovered] = useState(false);

  // Estado para mobile overlay
  const [mobileOpen, setMobileOpen] = useState(false);

  // Ref para detectar se é mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Refs para swipe gesture
  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);

  // Sidebar expandido se pinado OU hover (apenas desktop)
  const sidebarExpanded = isMobile ? mobileOpen : (isPinned || isHovered);

  // Sidebar luminance theme — only presets have a known luminance map.
  // Custom uploads and solid colors fall back to the light theme (white fg)
  // since we can't sample them here without extra work.
  // Overlay mode: only mobile actually overlays content now — desktop hover
  // pushes the main content (same as pinned), so the glass always sees the
  // wallpaper and the luminance-based theme applies in every desktop state.
  const isOverlayMode = sidebarExpanded && isMobile;
  const shouldForceDark = isOverlayMode && currentView !== 'home';
  const sidebarThemeVars = useMemo(() => {
    let theme: 'light' | 'dark';
    if (!wallpaperReady) {
      // Loading bg (#0f172a) is dark → light theme keeps text readable and
      // matches the post-load state for every preset (all luminance < 0.55),
      // so no theme flicker when the real wallpaper paints.
      theme = 'light';
    } else if (shouldForceDark) {
      theme = 'dark';
    } else {
      const valor = !homeBg || homeBg.tipo === 'preset' ? homeBg?.valor ?? 'default' : null;
      theme = valor ? resolvePresetTheme(valor, pessoa?.id).theme : 'light';
    }
    return {
      fg: themeToFgColor(theme),
      textShadow: themeToTextShadow(theme),
      logoShadow: themeToLogoShadow(theme),
      backdropFilter: themeToBackdropFilter(theme),
      widgetBackdropFilter: themeToWidgetBackdropFilter(theme),
      widgetTextShadow: themeToWidgetTextShadow(theme),
    };
  }, [wallpaperReady, shouldForceDark, homeBg, pessoa?.id]);

  // Detectar mudança de tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Swipe gesture para mobile
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!isMobile) return;
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
  }, [isMobile]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isMobile) return;
    touchCurrentX.current = e.touches[0].clientX;
  }, [isMobile]);

  const handleTouchEnd = useCallback(() => {
    if (!isMobile) return;

    const deltaX = touchCurrentX.current - touchStartX.current;
    const threshold = 50;

    // Swipe da borda esquerda para abrir
    if (touchStartX.current < 30 && deltaX > threshold && !mobileOpen) {
      setMobileOpen(true);
    }
    // Swipe para esquerda para fechar
    else if (deltaX < -threshold && mobileOpen) {
      setMobileOpen(false);
    }

    touchStartX.current = 0;
    touchCurrentX.current = 0;
  }, [isMobile, mobileOpen]);

  // Adicionar listeners de touch no documento
  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Handler de navegação
  const handleNavigate = (view: string, subview?: string, params?: Record<string, any>) => {
    // Em mobile, fechar o menu após navegar
    if (isMobile) {
      setMobileOpen(false);
    }
    onNavigate(view, subview, params);
  };

  // Handler de hover com delay para evitar flickering
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (isMobile) return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 150); // Pequeno delay para evitar flickering
  };

  // Limpar timeout ao desmontar
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="app-shell"
      style={{
        height: appHeight,
        overflow: 'hidden',
        ['--sidebar-fg' as string]: sidebarThemeVars.fg,
        ['--sidebar-text-shadow' as string]: sidebarThemeVars.textShadow,
        ['--sidebar-logo-shadow' as string]: sidebarThemeVars.logoShadow,
        ['--sidebar-backdrop-filter' as string]: sidebarThemeVars.backdropFilter,
        ['--widget-backdrop-filter' as string]: sidebarThemeVars.widgetBackdropFilter,
        ['--widget-text-shadow' as string]: sidebarThemeVars.widgetTextShadow,
      } as React.CSSProperties}
    >
      {/* Background shell-wide: aparece em todas as telas, atrás de tudo inclusive o menu */}
      <div
        className="home-background"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          ...homeBackgroundStyle.base,
          filter: homeBackgroundStyle.filter,
        }}
      />
      {homeBackgroundStyle.overlay && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            backgroundColor: homeBackgroundStyle.overlay.color,
            opacity: homeBackgroundStyle.overlay.opacity,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Side Navigation */}
      <SideNavigation
        isExpanded={sidebarExpanded}
        isPinned={isPinned}
        onTogglePin={() => setIsPinned(!isPinned)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        currentView={currentView}
        currentSubview={currentSubview}
        onNavigate={handleNavigate}
        onOpenDrawer={onOpenDrawer}
        isMobile={isMobile}
      />

      {/* Overlay para mobile */}
      {isMobile && mobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998
          }}
        />
      )}

      {/* Main Content — viewport for the per-view <PageShell> wrapper.
          Overflow is hidden here so scroll regions live inside each view,
          not at the shell level. Without this, tall lists (e.g. obras)
          would scroll the entire main area instead of the table itself. */}
      <main
        className="main-content"
        style={{
          position: 'relative',
          zIndex: 1,
          marginTop: 0,
          // Empurra o conteúdo sempre que o sidebar está expandido (pin ou hover).
          // +10px para compensar o left: 10px do menu e criar gap de 10px.
          marginLeft: isMobile ? 0 : (sidebarExpanded ? '290px' : '74px'),
          transition: 'margin-left 0.3s ease',
          height: appHeight,
          overflow: 'hidden',
          background: 'transparent'
        }}
      >
        {children}
      </main>

      {/* CSS para responsividade */}
      <style>{`
        @media (max-width: 1024px) {
          .main-content {
            margin-left: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}
